import React, { useRef } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { AtlasV2TrafficTopSectionV2 } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficTopSectionV2';
import AtlasV2TotalTrafficV2TotalTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficV2TotalTrafficSplineChart';
import AtlasV2TotalTrafficKeywordCountWithTotalTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficKeywordCountWithTotalTraffic';
import AtlasV2TotalTrafficV2ShareOfTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficV2ShareOfTrafficSplineChart';
import AtlasV2TotalTrafficDoubleDropdownTopEntity from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2TrafficDropdownTopEntityTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';
import AtlasV2FilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import {
  useBrandEntity,
  useBusinessUnitEntity,
  useCategoryEntity,
  useProductEntity,
  useSegmentEntity,
  useSubcategoryEntity
} from 'src/utils/Hooks/reduxSelectors';
import { AtlasV2TrafficProductGrid } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2ProductGrid';
import AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2BrandedUnbrandedSplineChart';
import { shouldShowBrandUnbranded } from 'src/utils/app';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';

/**
 * Atlas V2 Total Traffic V2 Page layout - Tab name: traffic subtab name: totalclicks
 */
export const AtlasV2TotalTrafficV2PageLayout = () => {
  const trafficSectionRef = useRef<HTMLDivElement>(null);
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isProductEntity } = useProductEntity();

  if (isRedirecting) return null;
  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <div
          ref={trafficSectionRef}
          style={{
            position: 'relative'
          }}
        >
          <BeaconChartWithLegend title="Traffic" showExportMenu={false}>
            <AtlasV2TrafficTopSectionV2 externalContainerRef={trafficSectionRef} />
          </BeaconChartWithLegend>
        </div>

        <div style={{ marginTop: '-49px' }}>
          <AtlasV2TotalTrafficV2TotalTrafficSplineChart />
        </div>

        <AtlasV2TotalTrafficV2ShareOfTrafficSplineChart />

        {/* /* share of total traffic by keyword topEntity Chart* */}
        {!isCategoryEntity && !isSubcategoryEntity && !isSegmentEntity && !isBusinessUnitEntity && !isBrandEntity && (
          <AtlasV2TrafficDropdownTopEntityTraffic
            groupByFields={[{ id: 'keyword', label: 'Keyword', indexName: 'traffic' }]}
            queryKey="totalClicks_singleDropdown"
            fields={[
              {
                fieldName: 'totalClicks',
                label: 'Total Traffic',
                metricType: 'totalClicks',
                metricTypeDisplay: METRICTYPE.VOLUME
              },
              {
                fieldName: 'totalClicksShare',
                label: 'Share of Total Traffic',
                metricType: 'totalClicks',
                metricTypeDisplay: METRICTYPE.PERCENT,
                isMarketShare: true
              }
            ]}
          />
        )}

        {/* Branded/ Unbranded SpineChart Traffic */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) &&
          shouldShowBrandUnbranded() && <AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart fieldName="totalClicks" />}

        {!isProductEntity && <AtlasV2TotalTrafficDoubleDropdownTopEntity />}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <AtlasV2FilterableStackedBarChart
            metricDropdownOptions={[
              {
                id: 'totalTraffic',
                label: 'Total Traffic',
                apiMetricFieldName: 'totalClicks',
                subtitleLabel: 'total traffic'
              },
              {
                id: 'totalTrafficPercent',
                label: '% of Total Traffic',
                apiMetricFieldName: 'totalClicks',
                isPercentageVisible: true,
                defaultMetric: 'totalTraffic',
                subtitleLabel: 'percentage of total traffic'
              }
            ]}
            groupByFieldOptions={[
              {
                id: 'keyword',
                label: 'Keyword'
              }
            ]}
          />
        )}

        <AtlasV2TotalTrafficKeywordCountWithTotalTraffic />

        {isBrandEntity && (
          <AtlasV2TotalTrafficDoubleDropdownTopEntity
            groupByFields={[
              { id: 'category', label: 'Category', indexName: 'traffic' },
              { id: 'subcategory', label: 'Subcategory', indexName: 'traffic' }
            ]}
          />
        )}

        {(isCategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <AtlasV2FilterableStackedBarChart
            metricDropdownOptions={[
              {
                id: 'totalTraffic',
                label: 'Total Traffic',
                apiMetricFieldName: 'totalClicks',
                subtitleLabel: 'total traffic'
              },
              {
                id: 'totalTrafficPercent',
                label: '% of Total Traffic',
                apiMetricFieldName: 'totalClicks',
                isPercentageVisible: true,
                defaultMetric: 'totalTraffic',
                subtitleLabel: 'percentage of total traffic'
              }
            ]}
            groupByFieldOptions={[
              ...(isAllCategoriesEntity || isSegmentEntity || isBusinessUnitEntity
                ? [
                    {
                      id: 'category',
                      label: 'Category'
                    }
                  ]
                : []),
              {
                id: 'subcategory',
                label: 'Subcategory'
              }
            ]}
          />
        )}

        <AtlasV2TrafficProductGrid trafficType="totalClicks" />
      </Flex>
    </BasePageContainer>
  );
};
